import type { AxiosError } from 'axios';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { api } from '../../api';
import { getGuestThreadId, removeGuestThreadId, saveGuestThreadId } from '../../api/guest';
import type { Advocate } from '../../models/advocate';
import type { AdvocateInChat, ChatUserConn, GuestChatEndedBy } from '../../models/chat';
import { GUEST_COMPONENT } from '../../models/chat';
import { ChatPage } from '../../models/chat';
import type { ErrorMessage } from '../../models/Error';
import { type ChatSessionFeedback, type ChatSessionHelpful, GuestChatActivityModalStatus } from '../../models/guest';
import type { AsyncThunkConfig } from '../../models/slice';
import { RaygunErrorHandlerService } from '../../services/raygun';
import type { AdvocateReportedChatAs } from '../advocate/AdvocateChatSlice';
import { getGuestBlockedStatus } from './guestSlice';
import { searchLepsAdvocates, setLepsPageStatus, setShowLepsTabBar } from './lepsSlice';
import { searchServiceProviders, setServicesPageStatus, setShowServicesTabBar } from './servicesSlice';

const { logError } = RaygunErrorHandlerService();

type GuestChatSliceType = {
  guestChatUserConn: ChatUserConn | undefined;
  howGuestChatEnded: GuestChatEndedBy | undefined;
  advocateReportedChatAs: AdvocateReportedChatAs | undefined;
  guestAdvocateSentFirstMsg: boolean;
  surveyThreadId: string | undefined;
  guestPromptShownTime: number | undefined;
  guestAdvocateIsOffline: boolean;
  advocateInChat: AdvocateInChat | undefined;
  guestChatActivityModalStatus: GuestChatActivityModalStatus;
  selectedAdvocateToChat: Advocate | undefined;
};

const initialState: GuestChatSliceType = {
  guestChatUserConn: undefined,
  howGuestChatEnded: undefined,
  advocateReportedChatAs: undefined,
  guestAdvocateSentFirstMsg: false,
  surveyThreadId: undefined,
  guestPromptShownTime: undefined,
  guestAdvocateIsOffline: false,
  advocateInChat: undefined,
  guestChatActivityModalStatus: GuestChatActivityModalStatus.IDLE,
  selectedAdvocateToChat: undefined,
};

type StartChat =
  | { advocate: AdvocateInChat; guest_chat_user_conn: ChatUserConn }
  | { advocate_unavailable: true }
  | false
  | undefined;
export const startChat = createAsyncThunk<StartChat, ChatPage, AsyncThunkConfig>(
  'guestChat/startChat',
  async (chatPage, thunkAPI) => {
    const forServiceProviders = chatPage === ChatPage.SERVICES;

    try {
      const advocate_id = thunkAPI.getState().guestChatSlice.selectedAdvocateToChat?.advocate_id;
      if (advocate_id) {
        const response = await api.post<StartChat>('/guest/v1_start_chat', { advocate_id });
        let component: GUEST_COMPONENT = GUEST_COMPONENT.CHAT;
        if (!response || 'advocate_unavailable' in response) {
          component = GUEST_COMPONENT.CHAT_SESSION_ERROR;
        }
        if (forServiceProviders) {
          thunkAPI.dispatch(setShowServicesTabBar(true));
          thunkAPI.dispatch(setServicesPageStatus(component));
        } else {
          thunkAPI.dispatch(setShowLepsTabBar(true));
          thunkAPI.dispatch(setLepsPageStatus(component));
        }
        return response;
      }
    } catch (e) {
      if (forServiceProviders) {
        thunkAPI.dispatch(setShowServicesTabBar(true));
        thunkAPI.dispatch(setServicesPageStatus(GUEST_COMPONENT.CHAT_SESSION_ERROR));
      } else {
        thunkAPI.dispatch(setShowLepsTabBar(true));
        thunkAPI.dispatch(setLepsPageStatus(GUEST_COMPONENT.CHAT_SESSION_ERROR));
      }
      logError(e, [chatPage, 'guestChatSlice', 'startChat']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const startDartChat = createAsyncThunk<StartChat, undefined, AsyncThunkConfig>(
  'guestChat/startDartChat',
  async (_, thunkAPI) => {
    try {
      const response = await api.post<StartChat>('/guest/v1_start_dart_chat');
      let component: GUEST_COMPONENT = GUEST_COMPONENT.CHAT;
      if (!response || 'advocate_unavailable' in response) {
        component = GUEST_COMPONENT.DART_CHAT_SESSION_ERROR;
      }
      thunkAPI.dispatch(setLepsPageStatus(component));
      return response;
    } catch (e) {
      thunkAPI.dispatch(setLepsPageStatus(GUEST_COMPONENT.DART_CHAT_SESSION_ERROR));
      logError(e, ['guestChatSlice', 'startDartChat']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const sendChatHelpful = createAsyncThunk<string | undefined, boolean, AsyncThunkConfig>(
  'guestChat/sendChatHelpful',
  async (helpful, thunkAPI) => {
    try {
      const ms_comm_thread_id = thunkAPI.getState().guestChatSlice.surveyThreadId;
      if (ms_comm_thread_id) {
        const params: ChatSessionHelpful = { ms_comm_thread_id, helpful };
        const response = await api.post<string>('/guest/v0_chat_helpful', params);
        return response;
      }
    } catch (e) {
      logError(e, ['guestChatSlice', 'sendChatHelpful']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const sendChatFeedback = createAsyncThunk<string | undefined, string | null | undefined, AsyncThunkConfig>(
  'guestChat/sendChatFeedback',
  async (feedback, thunkAPI) => {
    try {
      const guestChatSlice = thunkAPI.getState().guestChatSlice;
      const ms_comm_thread_id = guestChatSlice.surveyThreadId;
      const advocate_id = guestChatSlice.selectedAdvocateToChat?.advocate_id;
      if (feedback) {
        const params: ChatSessionFeedback = { ms_comm_thread_id, feedback, advocate_id };
        const response = await api.post<string>('/guest/v0_chat_feedback', params);
        return response; // guest_feedback id
      }
    } catch (e) {
      logError(e, ['guestChatSlice', 'sendChatFeedback']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const getGuestNewChat = createAsyncThunk<
  {
    advocate: Advocate;
    guest_chat_user_conn: ChatUserConn;
    chatPage: ChatPage;
  } | null,
  string,
  AsyncThunkConfig
>('guestChat/getGuestNewChat', async (thread_id, thunkAPI) => {
  try {
    const response = await api.post<{
      advocate: Advocate;
      guest_chat_user_conn: ChatUserConn;
    } | null>('/guest/v0_chat_request', { thread_id });
    if (response) {
      const chatPage = response.advocate.service_provider_organization_id ? ChatPage.SERVICES : ChatPage.LEPS;
      return { ...response, chatPage };
    }
    return null;
  } catch (e) {
    logError(e, ['guestChatSlice', 'getGuestNewChat']);
    return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
  }
});

export const getGuestPageStatusComponent = (endedBy: GuestChatEndedBy | undefined) => {
  switch (endedBy) {
    case 'AdvocateTimeout':
    case 'AdvocateDecline':
      return GUEST_COMPONENT.CHAT_SESSION_ERROR;
    default:
      return GUEST_COMPONENT.ADVOCATE_SELECT;
  }
};

export const checkGuestChatEnded = createAsyncThunk<GuestChatEndedBy | undefined, undefined, AsyncThunkConfig>(
  'guestChat/getGuestChatEnded',
  async (_, thunkAPI) => {
    try {
      const thread_id = thunkAPI.getState().guestChatSlice.guestChatUserConn?.threadId;
      if (thread_id) {
        const ended_by = await api.post<GuestChatEndedBy | null>('/guest/v0_get_chat_ended', { thread_id });
        if (ended_by) {
          thunkAPI.dispatch(endGuestChat({ ended_by }));
        }
      }
    } catch (e) {
      logError(e, ['guestChatSlice', 'getGuestChatEnded']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export type EndGuestChatParams = {
  thread_id?: string;
  ended_by?: GuestChatEndedBy;
  advocateReportedChatAs?: AdvocateReportedChatAs;
};
export const endGuestChat = createAsyncThunk<EndGuestChatParams | undefined, EndGuestChatParams, AsyncThunkConfig>(
  'guestChat/endGuestChat',
  async (params, thunkAPI) => {
    try {
      const response = params;
      if (params.thread_id) {
        response.advocateReportedChatAs = await api.post<AdvocateReportedChatAs | undefined>('/guest/v0_end_chat', {
          thread_id: params.thread_id,
          ended_by: params.ended_by,
        });
      }
      const advocateInChat = thunkAPI.getState().guestChatSlice.advocateInChat;
      const forServiceProviders = Boolean(advocateInChat?.service_provider_organization_id);
      const component = getGuestPageStatusComponent(response.ended_by);
      if (forServiceProviders) {
        thunkAPI.dispatch(setServicesPageStatus(component));
      } else {
        thunkAPI.dispatch(setLepsPageStatus(component));
      }
      return response;
    } catch (e) {
      logError(e, ['guestChatSlice', 'endGuestChat']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const endGuestLeftChat = createAsyncThunk<undefined, undefined, AsyncThunkConfig>(
  'guestChat/endGuestLeftChat',
  async (_, thunkAPI) => {
    try {
      const thread_id = await getGuestThreadId();
      if (thread_id) {
        await api.post<boolean>('/guest/v0_end_left_chat', { thread_id });
        removeGuestThreadId();
      }
    } catch (e) {
      logError(e, ['guestChatSlice', 'endGuestLeftChat']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const getGuestChatReportedByAdvocate = createAsyncThunk<
  AdvocateReportedChatAs | null | undefined,
  undefined,
  AsyncThunkConfig
>('guestChat/getGuestChatReportedByAdvocate', async (_, thunkAPI) => {
  try {
    const thread_id = thunkAPI.getState().guestChatSlice.guestChatUserConn?.threadId;
    if (thread_id) {
      const response = await api.post<AdvocateReportedChatAs | null>('/guest/v0_get_chat_reported_by_advocate', {
        thread_id,
      });
      return response;
    }
  } catch (e) {
    logError(e, ['guestChatSlice', 'getGuestChatReportedByAdvocate']);
    return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
  }
});

export const setPreChatFeeling = createAsyncThunk<string | undefined, number, AsyncThunkConfig>(
  'guestChat/setPreChatFeeling',
  async (feeling, thunkAPI) => {
    try {
      const thread_id = thunkAPI.getState().guestChatSlice.guestChatUserConn?.threadId;
      if (thread_id) {
        const response = await api.post<string>('/guest/v0_set_feeling_pre_chat', { thread_id, feeling });
        return response; // chat_id
      }
    } catch (e) {
      logError(e, ['guestChatSlice', 'setPreChatFeeling']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const setPostChatFeeling = createAsyncThunk<string | undefined, number, AsyncThunkConfig>(
  'guestChat/setPostChatFeeling',
  async (feeling, thunkAPI) => {
    try {
      const thread_id = thunkAPI.getState().guestChatSlice.surveyThreadId;
      if (thread_id) {
        const response = await api.post<string>('/guest/v0_set_feeling_post_chat', { thread_id, feeling });
        return response; // chat_id
      }
    } catch (e) {
      logError(e, ['guestChatSlice', 'setPostChatFeeling']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

// oxlint-disable-next-line typescript/no-invalid-void-type
export const handlePostChat = createAsyncThunk<void, boolean, AsyncThunkConfig>(
  'guestChat/handlePostChat',
  async (forServiceProviders, thunkAPI) => {
    thunkAPI.dispatch(clearGuestChatState());
    thunkAPI.dispatch(getGuestBlockedStatus());

    if (forServiceProviders) {
      thunkAPI.dispatch(searchServiceProviders());
      thunkAPI.dispatch(setServicesPageStatus(GUEST_COMPONENT.ADVOCATE_SELECT));
    } else {
      thunkAPI.dispatch(searchLepsAdvocates());
      thunkAPI.dispatch(setLepsPageStatus(GUEST_COMPONENT.ADVOCATE_SELECT));
    }
  },
);

/**
 * That slice is reset to the initial state when guests do the post-chat survey (PCS).
 * Don't add a state that should hold between chats.
 * Use guestSlice or appStatusSlice for that.
 */
export const guestChatSlice = createSlice({
  name: 'guestChat',
  initialState,
  reducers: {
    setGuestAdvocateSentFirstMsg: (state, action: PayloadAction<boolean>) => {
      state.guestAdvocateSentFirstMsg = action.payload;
    },
    setGuestChatUserConn: (state, action: PayloadAction<ChatUserConn | undefined>) => {
      state.guestChatUserConn = action.payload;
    },
    setGuestPromptShownTime: (state, action: PayloadAction<number | undefined>) => {
      state.guestPromptShownTime = action.payload;
    },
    setGuestAdvocateIsOffline: (state, action: PayloadAction<boolean>) => {
      state.guestAdvocateIsOffline = action.payload;
    },
    setGuestChatActivityModalStatus: (state, action: PayloadAction<GuestChatActivityModalStatus>) => {
      state.guestChatActivityModalStatus = action.payload;
    },
    setSelectedAdvocateToChat: (state, action: PayloadAction<Advocate | undefined>) => {
      state.selectedAdvocateToChat = action.payload;
    },
    clearGuestChatState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(startChat.fulfilled, (state, action) => {
        if (action.payload) {
          if ('advocate_unavailable' in action.payload) {
            state.guestAdvocateIsOffline = true;
          } else {
            state.guestChatActivityModalStatus = GuestChatActivityModalStatus.CHAT_LOADING;
            const guestChatUserConn = action.payload.guest_chat_user_conn;
            state.guestChatUserConn = guestChatUserConn;
            state.advocateInChat = action.payload.advocate;
            saveGuestThreadId(guestChatUserConn.threadId);
          }
        }
      })
      .addCase(startDartChat.fulfilled, (state, action) => {
        if (action.payload) {
          if ('advocate_unavailable' in action.payload) {
            state.guestAdvocateIsOffline = true;
          } else {
            state.guestChatActivityModalStatus = GuestChatActivityModalStatus.CHAT_LOADING;
            const guestChatUserConn = action.payload.guest_chat_user_conn;
            state.guestChatUserConn = guestChatUserConn;
            state.advocateInChat = action.payload.advocate;
            saveGuestThreadId(guestChatUserConn.threadId);
          }
        }
      })
      .addCase(getGuestNewChat.fulfilled, (state, action) => {
        if (
          !state.howGuestChatEnded &&
          ((!state.guestChatUserConn?.threadId && action.payload?.guest_chat_user_conn.threadId) ||
            (!state.guestAdvocateSentFirstMsg && action.payload?.guest_chat_user_conn.firstAdvocateMsgSent))
        ) {
          state.guestChatUserConn = action.payload.guest_chat_user_conn;
          state.guestAdvocateSentFirstMsg = Boolean(action.payload.guest_chat_user_conn.firstAdvocateMsgSent);
        }
      })
      .addCase(endGuestChat.fulfilled, (state, action) => {
        const { ended_by, advocateReportedChatAs } = action.payload ?? {};
        state.howGuestChatEnded = ended_by;
        state.advocateReportedChatAs = advocateReportedChatAs;

        if (
          ended_by === 'GuestReportedAbuse' ||
          advocateReportedChatAs === 'Abuse' ||
          advocateReportedChatAs === 'Emergency'
        ) {
          state.guestChatActivityModalStatus = GuestChatActivityModalStatus.CHAT_REPORTED;
        } else {
          switch (action.payload?.ended_by) {
            case 'AdvocateTimeout':
            case 'AdvocateDecline':
              break;
            case 'CancelledByGuest':
              // clearing it here to avoid assigning a value to surveyThreadId
              state.guestChatUserConn = undefined;
              break;
            default:
              state.guestChatActivityModalStatus = GuestChatActivityModalStatus.CHAT_POST_FEELING;
          }
        }

        if (state.guestChatUserConn?.threadId) {
          state.surveyThreadId = state.guestChatUserConn.threadId;
        }
        state.guestChatUserConn = undefined;
        state.guestAdvocateSentFirstMsg = false;
        state.guestPromptShownTime = undefined;
        removeGuestThreadId();
      });
  },
});

export const {
  setGuestAdvocateSentFirstMsg,
  setGuestChatUserConn,
  clearGuestChatState,
  setGuestPromptShownTime,
  setGuestAdvocateIsOffline,
  setGuestChatActivityModalStatus,
  setSelectedAdvocateToChat,
} = guestChatSlice.actions;
